import { useEffect } from "react";
import { useGetSettings } from "@workspace/api-client-react";
import { useI18n } from "@/lib/i18n";
import { setMetaTag, setLinkTag, setJsonLd, buildBreadcrumbsForPath, type Restorer } from "@/lib/dom-meta";

const PLATFORM_DEFAULT = "منصة نكسس كارت";
const TITLE_SEPARATOR = " · ";

export interface PageMetaInput {
  title?: string | null;
  description?: string | null;
  keywords?: string | null;
  ogImage?: string | null;
  canonical?: string | null;
  ogType?: string;
  titleStrategy?: "suffix" | "prefix" | "raw";
  scope?: "storefront" | "admin";
}

interface ResolvedSettings {
  storeName: string;
  storeDescription: string;
  globalTitle: string;
  globalDescription: string;
  globalKeywords: string;
  globalOgImage: string;
}

function pickByLang(en: unknown, ar: unknown, lang: string): string {
  const eng = typeof en === "string" ? en.trim() : "";
  const ara = typeof ar === "string" ? ar.trim() : "";
  if (lang === "ar") return ara || eng;
  return eng || ara;
}

function resolveSettings(s: any, lang: string): ResolvedSettings {
  return {
    storeName: typeof s?.storeName === "string" ? s.storeName.trim() : "",
    storeDescription: typeof s?.storeDescription === "string" ? s.storeDescription.trim() : "",
    globalTitle: pickByLang(s?.metaTitle, s?.metaTitleAr, lang),
    globalDescription: pickByLang(s?.metaDescription, s?.metaDescriptionAr, lang),
    globalKeywords: pickByLang(s?.metaKeywords, s?.metaKeywordsAr, lang),
    globalOgImage:
      (typeof s?.ogImageUrl === "string" && s.ogImageUrl.trim()) ||
      (typeof s?.logoUrl === "string" && s.logoUrl.trim()) ||
      "",
  };
}

/**
 * Centralised browser-tab title and SEO meta-tag manager for the storefront
 * SPA. Resolves admin-supplied per-page values with a transparent fallback to
 * the merchant's global settings (storeName / metaTitle / metaDescription /
 * metaKeywords / ogImageUrl), so:
 *
 * - When a page passes its own title/description/etc, those win.
 * - When a page passes nothing (or the admin left the per-entity fields
 *   blank), the global SEO from /api/settings is used.
 * - When the admin saves a new global title in the control panel, every
 *   currently-mounted page reactively updates because this hook subscribes
 *   to the same TanStack settings cache as the admin form invalidates.
 *
 * Use `scope: "admin"` for control-panel pages (only updates <title>; meta
 * tags are skipped because admin pages aren't indexed and we don't want to
 * pollute the OG/twitter graph from inside the dashboard).
 */
export function usePageMeta(input: PageMetaInput = {}): void {
  const { data: settings } = useGetSettings();
  const { lang } = useI18n();

  const {
    title,
    description,
    keywords,
    ogImage,
    canonical,
    ogType,
    titleStrategy,
    scope,
  } = input;

  useEffect(() => {
    if (typeof document === "undefined") return;
    const resolved = resolveSettings(settings || {}, lang);
    const siteName = resolved.storeName || PLATFORM_DEFAULT;

    const pageTitle = (title || "").trim();
    const strategy = titleStrategy || "suffix";

    let finalTitle: string;
    if (!pageTitle) {
      // No per-page title → use the merchant's global SEO title, falling
      // back to the store name.
      finalTitle = resolved.globalTitle || siteName;
    } else if (
      strategy === "raw" ||
      pageTitle === siteName ||
      pageTitle.includes(siteName)
    ) {
      finalTitle = pageTitle;
    } else if (strategy === "prefix") {
      finalTitle = `${siteName}${TITLE_SEPARATOR}${pageTitle}`;
    } else {
      finalTitle = `${pageTitle}${TITLE_SEPARATOR}${siteName}`;
    }

    document.title = finalTitle;

    // Admin pages: only sync the browser tab title. They sit behind auth so
    // there's no SEO/OG benefit to the meta soup, and rewriting og:* every
    // route would leak admin labels into any social-share crawlers that
    // somehow reached the dashboard.
    if (scope === "admin") return;

    const desc =
      (description || "").trim() ||
      resolved.globalDescription ||
      resolved.storeDescription;
    const kw = (keywords || "").trim() || resolved.globalKeywords;
    const og = (ogImage || "").trim() || resolved.globalOgImage;
    const canon =
      (canonical || "").trim() ||
      (typeof window !== "undefined" ? window.location.href : "");
    const finalOgType = ogType || "website";

    const cleanups: Restorer[] = [];
    cleanups.push(setMetaTag("description", desc));
    cleanups.push(setMetaTag("keywords", kw));
    cleanups.push(setMetaTag("og:title", finalTitle, "property"));
    cleanups.push(setMetaTag("og:description", desc, "property"));
    cleanups.push(setMetaTag("og:type", finalOgType, "property"));
    cleanups.push(setMetaTag("og:image", og, "property"));
    cleanups.push(setMetaTag("og:url", canon, "property"));
    cleanups.push(setMetaTag("og:site_name", siteName, "property"));
    cleanups.push(setMetaTag("og:locale", lang === "ar" ? "ar_SA" : "en_US", "property"));
    cleanups.push(setMetaTag("og:locale:alternate", lang === "ar" ? "en_US" : "ar_SA", "property"));
    cleanups.push(setMetaTag("twitter:card", og ? "summary_large_image" : "summary"));
    cleanups.push(setMetaTag("twitter:title", finalTitle));
    cleanups.push(setMetaTag("twitter:description", desc));
    cleanups.push(setMetaTag("twitter:image", og));
    cleanups.push(setLinkTag("canonical", canon));

    // Auto BreadcrumbList JSON-LD from the current pathname (skip on home).
    if (typeof window !== "undefined") {
      const homeLabel = lang === "ar" ? "الرئيسية" : "Home";
      const breadcrumbs = buildBreadcrumbsForPath(window.location.origin, window.location.pathname, homeLabel);
      if (breadcrumbs) {
        cleanups.push(setJsonLd("tenant-breadcrumbs", breadcrumbs));
      }
    }

    return () => {
      for (const fn of cleanups) {
        try { fn(); } catch { /* noop */ }
      }
    };
  }, [
    settings,
    lang,
    title,
    description,
    keywords,
    ogImage,
    canonical,
    ogType,
    titleStrategy,
    scope,
  ]);
}
